import { useHealthLive, useHealthReady } from '@/shared/api/generated/health/health';
import { ApiError } from '@/shared/api/http';
import { unwrap } from '@/shared/api/unwrap';
import { appConfig } from '@/shared/config';
import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';

import styles from './StatusPage.module.scss';

type Tone = 'ok' | 'pending' | 'error';

interface Probe {
  label: string;
  tone: Tone;
  value: string;
  note?: string | undefined;
}

function describeError(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.requestId === undefined
      ? error.detail
      : `${error.detail} · ${t.status.requestId}: ${error.requestId}`;
  }

  return error instanceof Error ? error.message : undefined;
}

/**
 * Экран состояния — первый и пока единственный.
 *
 * Его задача не «показать статус», а проверить всю цепочку шага 0.1: спека
 * сгенерирована из Go-типов → orval собрал хуки → фронт через прокси дошёл до
 * бэкенда → бэкенд дошёл до базы. Если здесь три зелёных строки, скелет
 * работает целиком, а не по частям.
 */
export function StatusPage() {
  const live = useHealthLive({ query: { select: unwrap } });
  const ready = useHealthReady({ query: { select: unwrap } });

  const probes: Probe[] = [
    {
      label: t.status.frontend,
      tone: 'ok',
      value: t.status.ok,
      note: `${t.status.environment}: ${appConfig.environment} · ${t.status.apiBase}: ${appConfig.apiBaseUrl}`,
    },
    {
      label: t.status.backend,
      tone: live.isPending ? 'pending' : live.isError ? 'error' : 'ok',
      value: live.isPending
        ? t.status.checking
        : live.isError
          ? t.status.unavailable
          : `${t.status.ok} · ${t.status.environment}: ${live.data.environment}`,
      note: live.isError ? describeError(live.error) : undefined,
    },
    {
      label: t.status.database,
      tone: ready.isPending ? 'pending' : ready.isError ? 'error' : 'ok',
      value: ready.isPending
        ? t.status.checking
        : ready.isError
          ? t.status.unavailable
          : t.status.ok,
      note: ready.isError ? describeError(ready.error) : undefined,
    },
  ];

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>{t.app.name}</p>
        <h1 className={styles.title}>{t.status.title}</h1>
        <p className={styles.subtitle}>{t.status.subtitle}</p>
      </header>

      <ul className={styles.list}>
        {probes.map((probe) => (
          <li key={probe.label} className={styles.row}>
            <span className={cx(styles.dot, styles[`dot--${probe.tone}`])} aria-hidden="true" />
            <span className={styles.label}>{probe.label}</span>
            <span className={styles.value}>
              {probe.value}
              {probe.note !== undefined && <span className={styles.note}>{probe.note}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
